import React, { useState, useEffect } from 'react';
import { formatDate } from '../../utils/date';
import { apiRequest } from '../../utils/api';
import { theme } from '../../theme';

interface UserInfo {
  id: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string;
}

interface FieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

interface ChangeLogEntry {
  id: string;
  entityType: string;
  entityId: string;
  changeType: 'created' | 'updated';
  userId: string | null;
  userName: string | null;
  changes: FieldChange[];
  timestamp: string;
}

interface ChangeHistoryItem {
  type: 'Feature' | 'Component' | 'Test Case';
  name: string;
  id: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface ChangeHistoryModalProps {
  item: ChangeHistoryItem;
  onClose: () => void;
}

// Helper to format field names for display
const formatFieldName = (field: string): string => {
  const fieldNames: Record<string, string> = {
    name: 'Name',
    title: 'Title',
    description: 'Description',
    milestone: 'Milestone',
    platforms: 'Platforms',
    qaseId: 'External ID',
    featureId: 'Feature',
    componentId: 'Component'
  };
  return fieldNames[field] || field.charAt(0).toUpperCase() + field.slice(1);
};

// Helper to format field values for display
const formatFieldValue = (value: string | null | boolean): string => {
  if (value === null || value === undefined || value === '') {
    return '(empty)';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
};

const ChangeHistoryModal: React.FC<ChangeHistoryModalProps> = ({
  item,
  onClose
}) => {
  const [users, setUsers] = useState<Record<string, UserInfo>>({});
  const [changeLogs, setChangeLogs] = useState<ChangeLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch users and change logs in parallel
        const entityType = item.type === 'Feature' ? 'feature' : item.type === 'Component' ? 'component' : 'testcase';
        
        const [userList, logs] = await Promise.all([
          apiRequest<UserInfo[]>('/api/users/for-sessions', {}, { useAuth: true }),
          apiRequest<ChangeLogEntry[]>(`/api/changelog/${entityType}/${item.id}`, {}, { useAuth: true }).catch(() => [])
        ]);
        
        const userMap: Record<string, UserInfo> = {};
        userList.forEach(user => {
          userMap[user.id] = user;
        });
        setUsers(userMap);
        setChangeLogs(logs);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [item]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const getUserName = (userId?: string | null, userName?: string | null): string => {
    if (userName) return userName;
    if (!userId) return 'Unknown';
    const user = users[userId];
    if (user) {
      return user.displayName;
    }
    return userId;
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 3000 }}>
      <div 
        className="card modal-content" 
        onClick={e => e.stopPropagation()} 
        style={{ maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h2 className="title-lg" style={{ marginBottom: '0.5rem' }}>Change History</h2>
            <p className="subtitle">{item.type}: {item.name}</p>
          </div>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            style={{ fontSize: '1.5rem' }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <p className="hint">Loading...</p>
        ) : changeLogs.length === 0 ? (
          <div className="card" style={{ background: 'var(--surface-elevated)', padding: '2rem', textAlign: 'center' }}>
            <p className="text-muted">No change history available for this item.</p>
            <p className="hint mt-sm">History tracking was added recently. Future changes will be recorded.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {changeLogs.map((log, index) => (
              <div 
                key={log.id} 
                className="card" 
                style={{ 
                  background: 'var(--surface-elevated)', 
                  padding: '1rem',
                  borderLeft: `3px solid ${log.changeType === 'created' ? theme.success : theme.cyanGlow}`
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.25rem' }}>
                      {log.changeType === 'created' ? '✨' : '✏️'}
                    </span>
                    <span style={{ fontWeight: 600, color: theme.textPrimary }}>
                      {log.changeType === 'created' ? 'Created' : 'Updated'}
                    </span>
                  </div>
                  <span style={{ fontSize: '0.875rem', color: theme.textSecondary }}>
                    {formatDate(log.timestamp, 'MMM dd, yyyy h:mm a')}
                  </span>
                </div>

                {/* User */}
                <div style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: theme.textSecondary }}>
                  by <strong style={{ color: theme.textPrimary }}>{getUserName(log.userId, log.userName)}</strong>
                </div>

                {/* Changes */}
                {log.changes && log.changes.length > 0 && (
                  <div style={{ 
                    background: 'var(--surface)', 
                    borderRadius: '4px', 
                    padding: '0.75rem',
                    fontSize: '0.875rem'
                  }}>
                    <div style={{ fontWeight: 500, marginBottom: '0.5rem', color: theme.textSecondary }}>
                      Changes:
                    </div>
                    {log.changes.map((change, idx) => (
                      <div 
                        key={idx} 
                        style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          padding: '0.5rem 0',
                          borderBottom: idx < log.changes.length - 1 ? `1px solid ${theme.gridLine}` : 'none'
                        }}
                      >
                        <div style={{ fontWeight: 500, color: theme.textPrimary, marginBottom: '0.25rem' }}>
                          {formatFieldName(change.field)}
                        </div>
                        {log.changeType === 'created' ? (
                          <div style={{ color: theme.success }}>
                            Set to: {formatFieldValue(change.newValue)}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ 
                              color: theme.textMuted, 
                              textDecoration: 'line-through',
                              background: 'rgba(255,100,100,0.1)',
                              padding: '0.125rem 0.25rem',
                              borderRadius: '2px'
                            }}>
                              {formatFieldValue(change.oldValue)}
                            </span>
                            <span style={{ color: theme.textMuted }}>→</span>
                            <span style={{ 
                              color: theme.success,
                              background: 'rgba(100,255,100,0.1)',
                              padding: '0.125rem 0.25rem',
                              borderRadius: '2px'
                            }}>
                              {formatFieldValue(change.newValue)}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeHistoryModal;
